import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { Card, CardBody, Row, Col } from "reactstrap";
import Select from "react-select";
import { toast } from "react-toastify";
import { Button, CardHeader, Table } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner, faTrash } from "@fortawesome/free-solid-svg-icons";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { customFilterOption } from "../../../../../utility/Utility";
import { useLazyGetMenuItemAllListQuery } from "../../../../../redux/services/setMenuService";
import { DEFAULT_IMAGE_URL } from "../../../../../constants/constans";
import BreadCrumbs from "../../../../../components/BreadCrumbs";
import ControlledRowTextField from "../../../../../components/ControlledRowTextField";
import ButtonComponent from "../../../../../components/ButtonComponent";
import {
  useUpdateProductionPlanMutation,
  useDeleteProductionPlanMutation,
  useCreateProductionPlanMutation,
  useLazyGetAllProductionPlansQuery,
} from "../../../../../redux/services/DistributionService";
import Swal from "sweetalert2";
import { ProductionPlanItem } from "../Add Production Plan";

export interface ProductionPlanFormData {
  restaurantId?: string;
  displayName: string;
  items: ProductionPlanItem[];
}

const EditProductionPlan = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { planId, planName, planData } = location.state || {};

  const defaultValues: ProductionPlanFormData = {
    displayName: planName || "",
    items: planData?.items || [],
  };

  const RestID = useSelector(
    (state: any) => state.restaurant.selectedRestaurantDetails.id
  );

  // Form handling
  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
    setError,
    trigger,
  } = useForm<ProductionPlanFormData>({
    defaultValues,
    mode: "onChange",
  });

  const [selectedOption, setSelectedOption] = useState<any>(null);
  const [selectedPortion, setSelectedPortion] = useState<any>(null);
  const [selectedItems, setSelectedItems] = useState<ProductionPlanItem[]>(
    planData?.items || []
  );
  const [plannedQty, setPlannedQty] = useState<number>(0);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [availableQty, setAvailableQty] = useState<number>(0);
  const [availableOptions, setAvailableOptions] = useState<any[]>([]);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [existingPlanNames, setExistingPlanNames] = useState<string[]>([]);
  const [backendError, setBackendError] = useState<string | null>(null);

  const [triggerGetMenuItemList, { isLoading, data: menuItemsData }] =
    useLazyGetMenuItemAllListQuery();
  const [updateProductionPlanMutation] = useUpdateProductionPlanMutation();
  const [deleteProductionPlanMutation] = useDeleteProductionPlanMutation();
  const [createProductionPlanMutation] = useCreateProductionPlanMutation();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [triggerAllPlans, { data: productionPlans }] =
    useLazyGetAllProductionPlansQuery();

  // Fetch existing production plan names
  useEffect(() => {
    if (RestID) {
      triggerAllPlans(RestID)
        .unwrap()
        .then((data) => {
          const planNames = data.map((plan: any) => plan.displayName);
          setExistingPlanNames(planNames);
        })
        .catch((error) => {
          console.error("Error fetching production plans:", error);
        });
    }
  }, [RestID, triggerAllPlans]);

  useEffect(() => {
    if (RestID) {
      triggerGetMenuItemList({ restaurantId: RestID });
    }
  }, [RestID, triggerGetMenuItemList]);

  useEffect(() => {
    if (planName) {
      setValue("displayName", planName);
      trigger("displayName"); // Trigger validation for initial value
    }
  }, [planName, setValue, trigger]);

  useEffect(() => {
    if (menuItemsData) {
      // Don't filter out items that are already added
      const options = menuItemsData.map((item: any) => ({
        value: item.itemId,
        displayName: item.displayName,
        abbreviation:item.abbreviation || "",
        label: (
          <div style={{ display: "flex", justifyContent: "left", gap: "5%" }}>
            <img
              src={item.imageUrl || DEFAULT_IMAGE_URL}
              alt=""
              width="30px"
              height="30px"
            />
            {item.productId} | {item.displayName}
          </div>
        ),
        description: item.description,
        price: item.price,
        imageUrl: item.imageUrl,
        productId: item.productId,
        itemType: item.itemType,
        category: item.category,
        portions: item.portions || [{ displayName: "REGULAR", additionalCost: 0 }]
      }));
      setAvailableOptions(options);
    }
  }, [menuItemsData]);

  // Get portion options for the selected item, filtering out already selected portions
  const getPortionOptions = () => {
    if (!selectedOption) return [];

    const portions = selectedOption.portions || [{ displayName: "REGULAR", additionalCost: 0 }];

    // Filter out portions that are already selected for this item
    const alreadySelectedPortions = selectedItems
      .filter(item => item.itemId === selectedOption.value)
      .map(item => item.portionSize);

    return portions
      .filter((portion: any) => !alreadySelectedPortions.includes(portion.displayName))
      .map((portion: any) => ({
        value: portion.displayName,
        label: portion.displayName
      }));
  };

  // Handle production plan update
  const updateProductionPlan = async (data: ProductionPlanFormData) => {
    try {
      setIsUpdating(true);
      setBackendError(null);

      const payload: ProductionPlanFormData = {
        displayName: data.displayName,
        restaurantId: RestID,
        items: selectedItems.map((item) => ({
          itemId: item.itemId,
          displayName: item.displayName,
          plannedQty: item.plannedQty,
          productId: item.productId,
          itemType: item.itemType,
          category: item.category,
          portionSize: item.portionSize || "REGULAR",
          unitPrice: item.unitPrice,
          abbreviation:item.abbreviation || "",
        })),
      };

      await updateProductionPlanMutation({
        planId: planId,
        restaurantId: RestID,
        displayName: payload.displayName,
        items: payload.items,
      }).unwrap();

      toast.success("Production Plan updated successfully");
      navigate("/distribution/orderManagement");
    } catch (error: any) {
      setBackendError(error?.data?.message || "Failed to update production plan");
      console.error("Error updating production plan:", error);
    } finally {
      setIsUpdating(false);
    }
  };

  // Handle production plan deletion
  const handleDeletePlan = async () => {
    if (planData?.isActive) {
      toast.error("Cannot delete active production plan");
      return;
    }

    const result = await Swal.fire({
      title: "Delete Production Plan",
      text: "Are you sure you want to delete this production plan?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        await deleteProductionPlanMutation({
          planId: planId,
          restaurantId: RestID,
        }).unwrap();
        toast.success("Production Plan deleted successfully");
        navigate("/distribution/orderManagement");
      } catch (error: any) {
        setBackendError(error?.data?.message || "Failed to delete production plan");
      }
    }
  };

  // Handle save as new plan
  const handleSaveAsNew = async (data: ProductionPlanFormData) => {
    // Trigger form validation
    const isValid = await trigger("displayName");
    if (!isValid) {
      return;
    }

    // Check for duplicate name
    if (existingPlanNames.includes(data.displayName)) {
      setError("displayName", {
        type: "manual",
        message: "This production plan name already exists. Please use a different name.",
      });
      return;
    }

    try {
      setIsCreatingNew(true);
      setBackendError(null);

      const payload: ProductionPlanFormData = {
        displayName: data.displayName,
        restaurantId: RestID,
        items: selectedItems.map((item) => ({
          itemId: item.itemId,
          displayName: item.displayName,
          plannedQty: item.plannedQty,
          productId: item.productId,
          itemType: item.itemType,
          category: item.category,
          portionSize: item.portionSize || "REGULAR",
          unitPrice: item.unitPrice,
          abbreviation:item.abbreviation || ""
        })),
      };

      await createProductionPlanMutation(payload).unwrap();

      toast.success("New Production Plan created successfully");
      navigate("/distribution/orderManagement");
    } catch (error: any) {
      console.error("Error creating production plan:", error);
      toast.error(error?.data?.message || "Failed to create production plan");
    } finally {
      setIsCreatingNew(false);
    }
  };

  const handleAdd = () => {
    if (selectedOption && selectedPortion) {
      const selectedPortionDetails = selectedOption.portions.find(
        (portion: any) => portion.displayName === selectedPortion.value
      );

      const additionalCost = selectedPortionDetails?.additionalCost || 0;
      const basePrice = selectedOption.price || 0;
      const totalPrice = basePrice + additionalCost;

      const newItem: ProductionPlanItem = {
        itemId: selectedOption.value,
        displayName: selectedOption.displayName,
        plannedQty: plannedQty,
        productId: selectedOption.productId,
        itemType: selectedOption.itemType,
        category: selectedOption.category,
        portionSize: selectedPortion.value,
        unitPrice: totalPrice,
        abbreviation:selectedOption.abbreviation || "",
      };
      setSelectedItems([...selectedItems, newItem]);
      setSelectedOption(null);
      setSelectedPortion(null);
      setPlannedQty(0);
      setAvailableQty(0);
      setErrorMessage(null);
    } else {
      setErrorMessage("Please select an item and portion to add.");
    }
  };

  const handleDelete = (index: number) => {
    const updatedItems = [...selectedItems];
    updatedItems.splice(index, 1);
    setSelectedItems(updatedItems);
  };

  const handleQuantityChange = (index: number, newQty: number) => {
    if (newQty >= 0) {
      const updatedItems = [...selectedItems];
      updatedItems[index] = { ...updatedItems[index], plannedQty: newQty };
      setSelectedItems(updatedItems);
    }
  };

const calculateTotalQuantity = () => {
  return selectedItems.reduce((total, item) => total + item.plannedQty, 0);
};


  return (
    <>
      <BreadCrumbs hasSubMenus={true} />
      <br />
      <Card>
        <CardHeader style={{ backgroundColor: "#fff" }}>
          <h4>Edit Production Plan</h4>
        </CardHeader>
        <CardBody>
          <form
            className="auth-login-form mt-3"
            onSubmit={handleSubmit(updateProductionPlan)}
          >
            <ControlledRowTextField
              label={"Production Plan Name"}
              inputName={"displayName"}
              inputType={"text"}
              placeholder={"Enter Production Plan Name"}
              isRequired={true}
              control={control}
              rules={{
                required: "Production Plan Name is required",
              }}
              defaultValue={planName}
            />
            {errors.displayName && (
              <span className="text-danger">
                {errors.displayName.message}
              </span>
            )}
            {backendError && (
              <span className="text-danger">{backendError}</span>
            )}

            <hr />
          </form>
        </CardBody>
      </Card>

      <Card className="mt-3">
        <CardBody>
          <Row className="align-items-center mt-3">
            <Col sm={4}>
              <Select
                value={selectedOption}
                onChange={(option) => {
                  setSelectedOption(option);
                  setSelectedPortion(null);
                }}
                options={availableOptions}
                placeholder={isLoading ? "Loading..." : "Search menu item"}
                isSearchable
                isDisabled={!availableOptions}
                loadingMessage={() => "Loading menu items..."}
                noOptionsMessage={() =>
                  isLoading ? "Loading..." : "No items found"
                }
                filterOption={customFilterOption}
              />
            </Col>
            <Col sm={3}>
              <Select
                value={selectedPortion}
                onChange={setSelectedPortion}
                options={getPortionOptions()}
                placeholder="Select portion"
                isSearchable={false}
                isDisabled={!selectedOption || getPortionOptions().length === 0}
              />
              {selectedOption && getPortionOptions().length === 0 && (
                <small
                  className="text-danger"
                  style={{
                    position: "absolute",
                    marginTop: "4px",
                    display: "block",
                  }}
                >
                  All portions for this item have been added
                </small>
              )}
            </Col>
            <Col sm={2}>
              {/* <input
                type="number"
                className="form-control"
                min="0"
                value={plannedQty}
                onChange={(e) => setPlannedQty(Number(e.target.value))}
                placeholder="Planned Qty"
              /> */}
              <input
                type="number"
                className="form-control"
                min="0"
                value={plannedQty === 0 ? '' : plannedQty}
                onChange={(e) => {
                  const value = e.target.value;
                  setPlannedQty(value === '' ? 0 : Number(value) || 0);
                }}
                onFocus={(e) => {
                  if (e.target.value === '0') {
                    e.target.value = '';
                  }
                }}
                onBlur={(e) => {
                  if (e.target.value === '') {
                    setPlannedQty(0);
                  }
                }}
                placeholder="0"
              />

            </Col>
            <Col sm={2} className="text-right">
              <ButtonComponent
                titel="+Add Item"
                type="button"
                onClick={handleAdd}
                isDisabled={!selectedOption || !selectedPortion || plannedQty < 1}
              />
            </Col>
          </Row>
          <br />
          <div className="selected-items-container">
            <h5>Selected Items</h5>
            <Table bordered className="mt-3" responsive>
              <thead>
                <tr>
                  <th>Product ID</th>
                  <th>Name</th>
                  <th>Portion Size</th>
                  <th>Planned Qty</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {selectedItems.map((item, index) => (
                  <tr key={index}>
                    <td>{item.productId}</td>
                    <td>{item.displayName}</td>
                    <td>{item.portionSize}</td>
                    <td>
                      <input
                        type="number"
                        className="form-control"
                        min="0"
                        value={item.plannedQty}
                        onChange={(e) =>
                          handleQuantityChange(index, Number(e.target.value))
                        }
                        style={{ width: "100px" }}
                      />
                    </td>
                    <td>
                      <Button
                        variant="light"
                        onClick={() => handleDelete(index)}
                      >
                        <FontAwesomeIcon
                          icon={faTrash}
                          title="Delete"
                          color="#F24E1E"
                        />
                      </Button>
                    </td>
                  </tr>
                ))}
                 {selectedItems.length > 0 && (
      <tr style={{ fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>
        <td colSpan={3} className="text-end">
          Total Quantity:
        </td>
        <td>{calculateTotalQuantity()}</td>
        <td></td>
      </tr>
    )}
              </tbody>
            </Table>
            {errorMessage && (
              <div className="text-danger mt-2">{errorMessage}</div>
            )}
          </div>
        </CardBody>
      </Card>

      <div className="d-flex justify-content-end m-4 gap-2">
        <ButtonComponent
          titel="Delete"
          type="button"
          onClick={handleDeletePlan}
        />
        {isCreatingNew ? (
          <ButtonComponent titel="Saving..." type="button" isDisabled={true} />
        ) : (
          <ButtonComponent
            titel="Save as New"
            type="button"
            onClick={handleSubmit(handleSaveAsNew)}
            isDisabled={!!errors.displayName}
          />
        )}

        {isUpdating ? (
          <ButtonComponent
            icon={<FontAwesomeIcon icon={faSpinner} spin />}
            titel="Updating..."
            isDisabled={true}
          />
        ) : (
          <ButtonComponent
            titel="Update"
            type="button"
            onClick={handleSubmit(updateProductionPlan)}
            isDisabled={selectedItems.length === 0}
          />
        )}
      </div>
    </>
  );
};

export default EditProductionPlan;